import { IllustrationName } from "Constants/constants";
import React, { useEffect } from "react";
import { getHeader } from "./productIntroTileUtils";

import { BaseTile } from "Components/BaseTile";
import { RedirectErrorPage } from "Components/RedirectErrorPage";
import { pageDisplayed } from "Store/analytics/analyticsSlice";
import { useCurrentCart } from "Store/cart";
import { useMarketingConsent } from "Store/consent/consentHooks";
import { setMarketingConsented } from "Store/consent/marketingConsentSlice";
import {
  useBrandingTheme,
  usePartnerAgreementNumber,
} from "Store/partners/partnerHooks";
import { Partner } from "Store/partners/partnerInterfaces";
import { FormState } from "Store/priceCalculation/priceCalculationInterfaces";
import { useTileTexts } from "Store/texts/hooks";
import { updateHuseierneMembershipStatusStart } from "Store/updateHuseierneMembershipStatus/slice";
import { AppPartName, stepName } from "Utils/analytics";
import { useScrollToTop } from "Utils/autoScrollUtils";
import { getCartIdFromCookie } from "Utils/cookies/cookiesUtil";
import { hasSpecifiedPartnerProductAndAgreementCodeMismatch } from "Utils/partnerUtils";
import { FormikChildren } from "Utils/types";
import { Formik } from "formik";
import { useDispatch } from "react-redux";
import { ObjectSchema } from "yup";
import { ProductBackendName } from "../../Products/productTypes";
import { useUserIdentification } from "../../userIdentificationHook";
import { UserIdentificationHandler } from "../UserIdentificationHandler";
import {
  withPartnerInitialValues,
  withPartnerValidationSchema,
} from "../partnerValidation";

interface Props extends FormikChildren {
  readonly illustrationName?: IllustrationName;
  readonly onSubmit?: (formValues?: FormState) => void;
  readonly initialValues?: any;
  readonly validationSchema?: ObjectSchema<any>;
  readonly productBackendName: ProductBackendName;
  readonly header?: string;
  readonly withPartnerValidation?: boolean;
}

export const ProductIntroTile = ({
  illustrationName,
  children,
  header,
  initialValues,
  validationSchema,
  onSubmit,
  productBackendName,
  withPartnerValidation = true,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const { id: partnerId } = useBrandingTheme();
  useScrollToTop();
  const partnerAgreementNumber = usePartnerAgreementNumber();
  const [{ checkPriceProductIntroTile: tileTexts }, lang] = useTileTexts();
  const userIdentificationEntity = useUserIdentification(productBackendName);
  const currentCart = useCurrentCart();
  const cartId = getCartIdFromCookie();

  const { consented: consented } = useMarketingConsent();

  useEffect(() => {
    if (!cartId && consented === undefined) {
      dispatch(setMarketingConsented(tileTexts.signOff.text[lang]));
    }
    dispatch(pageDisplayed({ pageName: stepName(1, AppPartName.CHECK_PRICE) }));
  }, []);

  const handleSubmit = (formValues: Record<string, any>): void => {
    if (onSubmit) {
      if (
        partnerId === Partner.HUSEIERNE &&
        "huseierneMembershipStatus" in formValues
      ) {
        dispatch(
          updateHuseierneMembershipStatusStart({
            customerRef:
              userIdentificationEntity.current?.newCustomerResponse
                .customerRef ?? "",
            huseierneMembershipStatus: formValues["huseierneMembershipStatus"],
          }),
        );
      }
      onSubmit(formValues);
    }
  };

  if (!userIdentificationEntity.current) {
    return (
      <UserIdentificationHandler
        userIdentificationEntity={userIdentificationEntity}
      />
    );
  }

  if (
    hasSpecifiedPartnerProductAndAgreementCodeMismatch(
      productBackendName,
      partnerAgreementNumber,
    )
  ) {
    return <RedirectErrorPage />;
  }

  return (
    <BaseTile
      header={
        header ||
        getHeader(productBackendName, userIdentificationEntity, tileTexts, lang)
      }
      illustrationName={illustrationName}
      maxWidth="528px"
    >
      <Formik
        initialValues={withPartnerInitialValues(
          partnerId,
          initialValues,
          currentCart,
          withPartnerValidation,
        )}
        enableReinitialize
        validationSchema={withPartnerValidationSchema(
          partnerId,
          validationSchema,
          withPartnerValidation,
        )}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {children}
      </Formik>
    </BaseTile>
  );
};
